import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertOctagon, RefreshCw, Trash2, Server, Chrome, ChevronRight, CheckCircle2 } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

/**
 * RecentErrorsPanel — unified server + client error stream for admins.
 *
 * Renders below the System Health card so admins have one place to see
 * "what's failing right now" without SSHing into a server or asking users
 * for console screenshots. Per-row "Resolve" marks an error as handled
 * without deleting the audit trail, and errors older than 14 days auto-
 * purge server-side.
 */
export const RecentErrorsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/errors/recent?limit=50&source=${source}`);
      setData(r?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recent errors');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const handleClear = async () => {
    if (!window.confirm('Clear all stored errors? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/errors/recent?source=${source}`);
      toast.success('Errors cleared');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear');
    }
  };

  const handleResolve = async (errorId) => {
    setResolvingId(errorId);
    try {
      await api.post(`/admin/errors/${errorId}/resolve`);
      toast.success('Marked as resolved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolvingId(null);
    }
  };

  const summary = data?.summary_24h || { server_count: 0, client_count: 0, top_failing_paths: [] };
  const errors = data?.errors || [];

  const relTime = (iso) => {
    if (!iso) return '—';
    try {
      const then = new Date(iso).getTime();
      const diff = Date.now() - then;
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins} min ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch { return iso; }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-errors-tab">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-rose-600" />
            Recent Errors
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Server 5xx + browser JS crashes, most recent first. Last 24h:
            <span className="ml-2 font-medium text-rose-700">{summary.server_count} server</span>,
            <span className="ml-1 font-medium text-amber-700">{summary.client_count} client</span>.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
            data-testid="errors-source-filter"
          >
            <option value="all">All sources</option>
            <option value="server">Server 5xx</option>
            <option value="client">Client (JS)</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="errors-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={handleClear} data-testid="errors-clear-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {/* Top failing paths pill row */}
        {summary.top_failing_paths?.length > 0 && (
          <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg">
            <div className="text-xs font-medium text-rose-900 mb-2">Top failing paths (24h)</div>
            <div className="flex flex-wrap gap-2">
              {summary.top_failing_paths.map((p) => (
                <Badge key={p.path} variant="outline" className="bg-white text-rose-800 border-rose-300 gap-1" data-testid={`error-top-path-${p.count}`}>
                  <span className="font-mono">{p.path}</span>
                  <span className="text-rose-500">×{p.count}</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500">Loading…</div>
        ) : errors.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm" data-testid="errors-empty">
            <div className="w-10 h-10 rounded-full bg-emerald-50 mx-auto mb-2 flex items-center justify-center">✓</div>
            No errors captured. 🎉
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="errors-list">
            {errors.map((err) => {
              const isExpanded = expanded === err.error_id;
              const isServer = err.source === 'server';
              return (
                <div key={err.error_id} className="py-3" data-testid={`error-row-${err.error_id}`}>
                  <button
                    type="button"
                    className="w-full text-left flex items-start gap-3 hover:bg-slate-50 rounded p-1 -m-1"
                    onClick={() => setExpanded(isExpanded ? null : err.error_id)}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isServer ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'}`}>
                      {isServer ? <Server className="w-4 h-4" /> : <Chrome className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isServer && (
                          <>
                            <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">{err.status_code}</Badge>
                            <code className="text-xs font-mono text-slate-700">{err.method} {err.path}</code>
                          </>
                        )}
                        {!isServer && (
                          <code className="text-xs font-mono text-slate-700 truncate">{err.tab_name || 'App'} · {err.url}</code>
                        )}
                      </div>
                      <div className="text-sm text-slate-800 mt-1 truncate">{err.message}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {err.exception_type ? `${err.exception_type} · ` : ''}
                        {err.user_email || 'anon'} · {relTime(err.timestamp)}
                        {err.duration_ms != null && ` · ${err.duration_ms}ms`}
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="ml-11 mt-2 space-y-2">
                      {err.stack && (
                        <details open>
                          <summary className="text-xs text-slate-500 cursor-pointer">Stack trace</summary>
                          <pre className="mt-1 text-[11px] font-mono bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto max-h-64">{err.stack}</pre>
                        </details>
                      )}
                      {err.component_stack && (
                        <details>
                          <summary className="text-xs text-slate-500 cursor-pointer">Component stack</summary>
                          <pre className="mt-1 text-[11px] font-mono bg-slate-100 text-slate-800 p-3 rounded overflow-x-auto max-h-40">{err.component_stack}</pre>
                        </details>
                      )}
                      {err.request_id && <div className="text-[11px] text-slate-500">request_id: <code>{err.request_id}</code></div>}
                      {err.ip_address && <div className="text-[11px] text-slate-500">ip: <code>{err.ip_address}</code></div>}
                      {err.user_agent && <div className="text-[11px] text-slate-500 truncate">ua: {err.user_agent}</div>}
                      <div className="pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={(e) => { e.stopPropagation(); handleResolve(err.error_id); }}
                          disabled={resolvingId === err.error_id}
                          data-testid={`error-resolve-btn-${err.error_id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          {resolvingId === err.error_id ? 'Resolving…' : 'Mark resolved'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentErrorsPanel;
